/**
 * Navbar indicator showing backend sync-service health.
 * Hidden when the sync-service is not deployed/reachable.
 * Shows: green cloud (synced), amber cloud (pending), red cloud (offline/failed).
 * Click when issues exist to see a dropdown of failed entries with retry/dismiss actions.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Cloud, CloudOff, RefreshCw, RotateCcw, X, ChevronDown } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { useSyncStatus } from '../hooks/useSyncStatus';
import syncService, { SyncQueueEntry } from '../services/syncService';

export const SyncStatusIndicator: React.FC = () => {
  const { t } = useLanguage();
  const { status, available, triggering, triggerSync, refresh } = useSyncStatus();
  const [showDropdown, setShowDropdown] = useState(false);
  const [failedEntries, setFailedEntries] = useState<SyncQueueEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Position dropdown below the button
  const updatePosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showDropdown]);

  // Auto-refresh failed entries when dropdown is open and issue count changes
  const issueCount = (status?.failed_count ?? 0) + (status?.conflict_count ?? 0);
  useEffect(() => {
    if (showDropdown && issueCount > 0) {
      fetchFailedEntries();
    } else if (showDropdown && issueCount === 0) {
      setShowDropdown(false);
    }
  }, [issueCount]); // eslint-disable-line react-hooks/exhaustive-deps

  // Don't render if sync-service is not reachable
  if (!available || !status) return null;

  const hasIssues = issueCount > 0;
  const hasPending = status.pending_count > 0;

  // Check pull freshness: stale if last pull > 2 min ago or failed
  const pullStaleMs = 2 * 60 * 1000;
  const lastPullAge = status.last_pull_at ? Date.now() - new Date(status.last_pull_at).getTime() : Infinity;
  const pullFailed = status.last_pull_status === 'failed';
  const pullStale = lastPullAge > pullStaleMs;

  // Build tooltip with last pull info
  const formatPullAgo = () => {
    if (!status.last_pull_at) return t('sync.neverPulled') || 'Never pulled';
    const secs = Math.floor(lastPullAge / 1000);
    if (secs < 60) return `${secs}s ago`;
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m ago`;
    return `${Math.floor(mins / 60)}h ${mins % 60}m ago`;
  };
  const tooltip = `${t('sync.lastPull') || 'Last pull'}: ${formatPullAgo()}${pullFailed ? ` (${t('sync.failed') || 'failed'})` : ''}`;

  // Determine state: offline > failed > pull-stale > pending > synced
  let icon: React.ReactNode;
  let label: string;
  let colorClasses: string;

  if (!status.is_online) {
    icon = <CloudOff className="h-4 w-4" />;
    label = t('sync.cloudOffline');
    colorClasses = 'text-red-500';
  } else if (hasIssues) {
    icon = <Cloud className="h-4 w-4" />;
    label = `${issueCount} ${t('sync.issues')}`;
    colorClasses = 'text-red-500';
  } else if (pullFailed) {
    icon = <Cloud className="h-4 w-4" />;
    label = t('sync.pullFailed') || 'Pull failed';
    colorClasses = 'text-red-500';
  } else if (hasPending || triggering) {
    icon = <RefreshCw className={`h-4 w-4 ${triggering ? 'animate-spin' : ''}`} />;
    label = hasPending ? `${status.pending_count} ${t('sync.pending')}` : t('sync.syncing');
    colorClasses = 'text-amber-500';
  } else if (pullStale) {
    icon = <Cloud className="h-4 w-4" />;
    label = t('sync.pullStale') || 'Pull stale';
    colorClasses = 'text-amber-500';
  } else {
    icon = <Cloud className="h-4 w-4" />;
    label = t('sync.synced');
    colorClasses = 'text-green-500';
  }

  const fetchFailedEntries = async () => {
    setLoading(true);
    try {
      const data = await syncService.getQueue({ status: 'failed', limit: 20 });
      setFailedEntries(data.items);
    } catch {
      setFailedEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = async () => {
    if (hasIssues) {
      if (!showDropdown) {
        updatePosition();
        setShowDropdown(true);
        await fetchFailedEntries();
      } else {
        setShowDropdown(false);
      }
    } else {
      await triggerSync();
    }
  };

  const handleRetry = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await syncService.retry(id);
    await fetchFailedEntries();
    await refresh();
  };

  const handleDismiss = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await syncService.dismiss(id);
    await fetchFailedEntries();
    await refresh();
  };

  const handleRetryAll = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const ids = failedEntries.map(entry => entry.id);
    if (ids.length > 0) {
      await syncService.bulkRetry(ids);
      await fetchFailedEntries();
      await refresh();
    }
  };

  const handleDismissAll = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const ids = failedEntries.map(entry => entry.id);
    if (ids.length > 0) {
      await syncService.bulkDismiss(ids);
      await fetchFailedEntries();
      await refresh();
    }
  };

  // Format table name for display (e.g. "ics.hte_lot_scan_detail" → "lot_scan_detail")
  const shortTable = (name: string) => name.replace(/^(production|ics)\./, '');

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={handleClick}
        disabled={triggering}
        className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium select-none hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${colorClasses}`}
        title={tooltip}
      >
        {icon}
        <span className="hidden sm:inline">{label}</span>
        {hasIssues && <ChevronDown className="h-3 w-3" />}
      </button>

      {showDropdown && createPortal(
        <div
          ref={dropdownRef}
          style={{ position: 'fixed', top: dropdownPos.top, right: dropdownPos.right }}
          className="w-96 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
              {t('sync.failedEntries') || 'Failed Sync Entries'} ({failedEntries.length})
            </span>
            <div className="flex gap-1">
              {failedEntries.length > 0 && (
                <>
                  <button
                    onClick={handleRetryAll}
                    className="text-xs px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800"
                  >
                    {t('sync.retryAll') || 'Retry All'}
                  </button>
                  <button
                    onClick={handleDismissAll}
                    className="text-xs px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    {t('sync.dismissAll') || 'Dismiss All'}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Entry list */}
          <div className="max-h-72 overflow-y-auto">
            {loading ? (
              <div className="px-3 py-4 text-center text-xs text-gray-500 dark:text-gray-400">
                <RefreshCw className="h-4 w-4 animate-spin mx-auto mb-1" />
                Loading...
              </div>
            ) : failedEntries.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-gray-500 dark:text-gray-400">
                {t('sync.noFailedEntries') || 'No failed entries'}
              </div>
            ) : (
              failedEntries.map((entry) => (
                <div
                  key={entry.id}
                  className="px-3 py-2 border-b border-gray-100 dark:border-gray-700 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-750"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-medium text-gray-900 dark:text-white truncate">
                          {shortTable(entry.table_name)}
                        </span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 uppercase">
                          {entry.operation}
                        </span>
                      </div>
                      <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 font-mono truncate">
                        key: {entry.natural_key}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={(e) => handleRetry(entry.id, e)}
                        className="p-1 rounded hover:bg-blue-100 dark:hover:bg-blue-900 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                        title={t('sync.retry') || 'Retry'}
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={(e) => handleDismiss(entry.id, e)}
                        className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        title={t('sync.dismiss') || 'Dismiss'}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
